import IninBase from './IninBase';
import { debounce } from 'debounce';

export default class IninIPC extends IninBase {
  constructor(...args) {
    super(...args);
    this.selected = -1;
    this.hovered = -1;
    this.clickedRecently = false;
  }

  highlightItems(objectIds, enable, color, shine) {
    let animated = true;
    objectIds.forEach(objectId => {
      const EMISSIVE = this.cortona.app.PROPERTY_EMISSIVE_COLOR;
      const DIFFUSE = this.cortona.app.PROPERTY_DIFFUSE_COLOR;
      if (enable) {
        this.cortona.app.setObjectPropertyf(objectId, DIFFUSE, animated, ...color);
        this.cortona.app.setObjectPropertyf(objectId, EMISSIVE, animated, shine, shine, shine);
      } else {
        this.cortona.app.restoreObjectProperty(objectId, DIFFUSE, animated);
        this.cortona.app.restoreObjectProperty(objectId, EMISSIVE, animated);
      }
    });
  }

  changeItemsTransparency(objectIds, enable) {
    let animated = true;
    let transparency = 0.95;
    objectIds.forEach(objectId => {
      let property = this.cortona.app.PROPERTY_TRANSPARENCY;
      enable
        ? this.cortona.app.setObjectPropertyf(objectId, property, animated, transparency)
        : this.cortona.app.restoreObjectProperty(objectId, property, animated);
    });
  }

  changeItemsVisibility(objectIds, visible, animated = true) {
    // console.log('CHANGING VISIBILITY TO ', visible, ' FOR ', objectIds)
    objectIds.forEach(objectId => {
      // if (animated) this.cortona.app.setObjectPropertyf(objectId, this.cortona.app.PROPERTY_TRANSPARENCY, true, !visible)
      this.cortona.app.setObjectPropertyf(objectId, this.cortona.app.PROPERTY_VISIBILITY, false, !visible);
    });
  }

  getObjectsForRow(rowIndex) {
    let row = this.cortona.app.ipc.interactivity.getRowByIndex(rowIndex);
    // let children = this.cortona.app.ipc.interactivity.getChildren(row)
    // let objectIds = [row].concat(children || [])
    let rowIds = this.getAllChildren(row);
    // console.log('was:',objectIds,'is:',rowIds)
    let names = rowIds.flatMap(id => this.cortona.app.ipc.interactivity.getObjectsNamesByRow(id));
    return names.map(nm => this.cortona.app.getObjectWithName(nm));
  }

  //takes row returned by app.ipc.interactivity.getRowByIndex
  getAllChildren(row) {
    let children = this.cortona.app.ipc.interactivity.getChildren(row);
    return [row].concat(children?.flatMap(ch => this.getAllChildren(ch)) || []);
  }

  getItemInfo(rowIndex) {
    let row = this.cortona.app.ipc.interactivity.getRowByIndex(rowIndex);
    let info = this.cortona.app.ipc.interactivity.getItemInfo(row);
    return {
      index: rowIndex,
      partName: info.part.metadata.DFP,
      partNumber: info.part.metadata.PNR,
      quantity: Number(info.metadata.QNA),
    };
  }

  fitIntoView(rowIndex) {
    if (rowIndex >= 0) {
      let objects = this.getObjectsForRow(rowIndex);
      this.cortona.app.fitObjectsInView(objects, true);
    } else {
      this.cortona.app.fitSceneInView(true);
    }
  }

  getIndexByGlobalRow(n) {
    let inter = this.cortona.app.ipc.interactivity;
    return n >= 0 ? (this.isItemActive(n) ? inter.getIndexByRow(n) : this.getIndexByGlobalRow(inter.getParent(n))) : -1;
  }

  isItemActive(n) {
    let currentSheet = this.cortona.app.ipc.getCurrentSheet().id;
    return currentSheet && this.cortona.app.ipc.interactivity.getSheetInfo(currentSheet).items.indexOf(n) >= 0;
  }

  name2row(name) {
    // let obj = this.cortona.app.getObjectWithName(name)
    let globalRow = this.cortona.app.ipc.interactivity.getRowByObjectName(name);
    return this.getIndexByGlobalRow(globalRow);
  }

  setHovered = debounce(hovered => {
    if (this.hovered != hovered) {
      this.hovered = hovered;
      this.hoverRow(hovered);
    }
  }, 100);

  circlePath(cx, cy, r) {
    return `M ${cx} ${cy} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${r * 2},0`;
  }

  fixSvgHotspotPaths() {
    let paths = this.cortona.app.drawing.svg.querySelectorAll(`a > path`);
    paths.forEach(path => {
      if (!path.hasAttribute('data-circle')) {
        let box = path.getBBox();
        let d = this.circlePath(box.x + box.width / 2, box.y + box.height / 2, Math.max(box.width, box.height, 30));
        path.setAttribute('d', d);
        path.setAttribute('data-circle', true);
      }
    });
  }

  toggleSvgHotspotClass(index, className) {
    let itemId = this.cortona.app.ipc.interactivity.getItemByIndex(index);
    this.cortona.app.drawing.svg.querySelectorAll('a[name]').forEach(hotspot => {
      let region = hotspot.querySelector('.hotspot-region');
      if (region) region.classList.toggle(className, hotspot.getAttribute('name') == itemId);
      let outlines = hotspot.querySelectorAll('path');
      outlines.forEach(outline => {
        outline.classList.toggle('hover', hotspot.getAttribute('name') == itemId);
      });
    });
  }

  addListeners() {
    let events = [
      'app.ipc.didSelectSheet',
      'touch.didObjectClick',
      'app.drawing.didSelectHotspot',
      'app.ipc.didChangeItemVisibility',
      'app.ipc.didStopSheetTransitionAnimation',
      'app.ipc.willStartSheetTransitionAnimation',
      'app.ipc.didDrawingDisplayMode',
    ];
    events.forEach(event => {
      let methodName = event.replaceAll(/(\.\w)/g, v => v.substr(1).toUpperCase()); //eg. appIpcDidSelectSheet
      this.cortona.on(event, (...args) => {
        this[methodName](...args);
      });
    });

    // this.cortona.on("core.onLoad", () => {
    //     console.log('changing display mode')

    // })
    this.cortona.on('app.didFinishLoadDocument', () => this.didFinishLoadDocument());
    this.cortona.on('touch.didObjectEnter', (id, name) => {
      this.setHovered(this.name2row(name));
    });
    this.cortona.on('touch.didObjectOut', (id, name) => {
      this.setHovered(-1);
    });
    this.cortona.on('touch.didPointerUp', () => {
      if (this.clickedRecently) {
        this.fitIntoView(this.hovered);
      }
      this.clickedRecently = true;
      setTimeout(() => (this.clickedRecently = false), 300);
    });
    this.cortona.on('app.drawing.didHoverHotspot', (row, hovered) => {
      let index = -1;
      if (hovered) index = this.cortona.app.ipc.interactivity.getIndexByItem(row);
      this.setHovered(index);
    });
    this.cortona.on('app.drawing.didFinishLoadDrawing', name => {
      if (name?.endsWith('.svg')) this.fixSvgHotspotPaths();
    });
  }

  afterEngineLoad() {
    this.checkIllustrationTypeSvgOnly();
  }

  didFinishLoadDocument(fromCache = false) {
    this.reset();
    this.dispatchEvents(fromCache);
    if (this.cortonaFrame) this.external.showFrame();
  }

  dispatchEvents(fromCache = false) {
    if (this.isVariantNavi() && !fromCache) {
      this.changeItemsVisibility(this.cortona.app.getChildObjects(), false, false);
    }
    this.emit('navigation', this.isNavigationPage());
    this.emit('isVariantNavi', this.isVariantNavi());
    this.emit('sheets', this.cortona.app.modelInfo.sheets);
    let currentSheet = this.cortona.app.ipc.getCurrentSheet();
    if (currentSheet && currentSheet.id) this.appIpcDidSelectSheet(currentSheet);
    // if (this.isVariantNavi() && fromCache) {
    //     this.emit('selectFirstRowOnVariantNavi', undefined, true);
    // }
  }

  isNavigationPage() {
    return this.cortona.app.ipc.interactivity.getProjectMetadata()._6E1470EBC3BE4F0B9775ADDC98BE1060 == 'Navigation';
  }

  isVariantNavi() {
    return this.cortona.app.ipc.interactivity.getProjectMetadata().NavigationType == 'Variants';
  }

  checkIllustrationTypeSvgOnly() {
    let svgonly = this.isIllustrationTypeSvgOnly();
    if (svgonly) this.cortona.app.ipc.toggleDrawingDisplayMode(true);
    this.emit('svgOnly', svgonly);
    this.emit('svgMode', this.isDrawingVisible());
  }

  isIllustrationTypeSvgOnly() {
    return this.cortona.app.getOptions().IllustrationType >= 2;
  }

  isDrawingVisible() {
    return this.cortona.app.drawing?.isVisible();
  }

  appIpcDidSelectSheet(sheet) {
    this.selectRow(-1);
    let ipc = this.cortona.app.ipc;
    sheet.drawing = ipc.getCurrentSheet().drawing;

    this.emit('sheetSelected', sheet, true);

    // let table = ipc.interactivity.json.ipc.figure["ipc-table"].table;
    // let headers = table.thead.row.entry
    //     .slice(1)
    //     .map((e, i) => ({ text: e.para && e.para["#text"], value: `col${i}`, key: e['@attributes']['column-id'], width: e['@attributes']['width'] }));
    // let items = table.tbody.row.map(r =>
    //     r.entry.slice(1).reduce((a, c, i) => ((a[`col${i}`] = c.para?.["#text"] || c.cmdtext?.["#text"]), a), {
    //         visible: true,
    //     })
    // );
    // items.forEach((item, index) => {
    //     let trueIndex = ipc.interactivity.getRowByIndex(index);
    //     let info = ipc.interactivity.getItemInfo(trueIndex);
    //     item.url = info?.metadata?._B479653540A9423B8170B31E38783811;
    // });
    // this.emit("tableHeaders", headers);
    // this.emit("tableItems", items);
  }

  appIpcWillStartSheetTransitionAnimation() {
    this.emit('sheetLocked', true);
  }

  appIpcDidStopSheetTransitionAnimation() {
    this.emit('sheetLocked', false);
  }

  appIpcDidDrawingDisplayMode(svgMode) {
    this.emit('svgMode', svgMode);
  }

  touchDidObjectClick(id, name) {
    this.selectRow(this.hovered);
  }

  appDrawingDidSelectHotspot(row, smth) {
    this.selectRow(this.hovered);
  }

  appIpcDidChangeItemVisibility(hidden, rows) {
    this.emit('changeVisibility', { hidden, rows }, true);
  }

  changeSheet(sheet) {
    this.reset();
    this.cortona.app.ipc.setCurrentSheet(sheet.id, true);
  }

  hoverRow(index) {
    this.emit('hovered', index);
    let outside = index == -1;
    let allRows = this.cortona.app.ipc.interactivity.getSheetInfo().items;
    let indexObjects = this.getObjectsForRow(index);
    allRows.forEach((_, i) => {
      let objects = index == i ? indexObjects : this.getObjectsForRow(i).filter(ob => !indexObjects.includes(ob));
      if (this.isVariantNavi()) {
        if (!outside) this.changeItemsVisibility(objects, i == index, true);
      } else {
        if (i == index && !outside) {
          // console.log("Index objects", i, objects);
        }
        this.changeItemsTransparency(objects, i != index && !outside);
      }
    });
    this.toggleSvgHotspotClass(index, 'hovered');
  }

  selectRow(index) {
    let allRows = this.cortona.app.ipc.interactivity.getSheetInfo().items;
    let color = this.colors.accent;
    let wasSelected = this.selected;
    let newSelected = wasSelected == index ? -1 : index;
    let indexObjects = this.getObjectsForRow(index);
    allRows.forEach((_, i) => {
      let isSelected = i == newSelected;
      let objects = index == i ? indexObjects : this.getObjectsForRow(i).filter(ob => !indexObjects.includes(ob));
      // this.cortona.app.setSelectedObjects(objects, true);
      this.highlightItems(objects, isSelected, color, 0.2);
    });
    this.toggleSvgHotspotClass(newSelected, 'selected');
    this.emit('selected', newSelected);
    this.selected = newSelected;
  }

  setVisibility(item) {
    this.cortona.app.ipc.toggleItemVisibility(item.viewPartSequence);
  }

  reset() {
    this.cortona.app.ipc.resetCurrentSheet(true);
  }

  toggleDrawingMode() {
    let on = this.cortona.app.ipc.toggleDrawingDisplayMode();
    if (on) this.toggleSvgHotspotClass(this.selected, 'selected');
  }
}